import React, { FC, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import { useDispatch } from 'react-redux';
import { setTriggerType } from '../../actions/filterActions';
import { pageTypeConstants } from '../../constants/pageTypeConstants';
import { Button } from 'dibs-elements/exports/Button';
import Close from 'dibs-icons/exports/Close';

import { SbSharedRefineMenuFilters } from '../SbSharedRefineMenu/SbSharedRefineMenuFilters/SbSharedRefineMenuFilters';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import { SbRespRefineMenuFilterSortFacet } from './SbRespRefineMenuFilterSortFacet';
import { SbRespRefineMenuDrawerHeader } from './SbRespRefineMenuDrawerHeader';
import { SbSharedRefineMenuDisplayPrefsSelect } from '../SbSharedRefineMenu/SbSharedRefineMenuDisplayPrefsSelect/SbSharedRefineMenuDisplayPrefsSelect';
import { SbRespRefineMenuDrawer_itemSearch } from './__generated__/SbRespRefineMenuDrawer_itemSearch.graphql';
import { SbRespRefineMenuDrawer_viewer } from './__generated__/SbRespRefineMenuDrawer_viewer.graphql';
import { SbRespRefineMenuDrawer_user } from './__generated__/SbRespRefineMenuDrawer_user.graphql';

import styles from './SbRespRefineMenuDrawer.scss';

function lockGlobalScroll(): void {
    document.body.classList.add(styles.lockScroll);
}

function unlockGlobalScroll(): void {
    document.body.classList.remove(styles.lockScroll);
}

type Props = {
    currency: string;
    itemSearch: SbRespRefineMenuDrawer_itemSearch;
    viewer: SbRespRefineMenuDrawer_viewer;
    user: SbRespRefineMenuDrawer_user;
    isClient: boolean;
    isTrade: boolean;
    isOpen: boolean;
    closeDrawer: () => void;
};

const SbRespRefineMenuDrawerComponent: FC<Props> = ({
    currency,
    itemSearch,
    viewer,
    user,
    isClient,
    isTrade,
    isOpen,
    closeDrawer,
}) => {
    const { filters, totalResults } = itemSearch;
    const dispatch = useDispatch();

    const escCloseDrawer = useCallback(
        e => {
            if (e.key === 'Escape') {
                closeDrawer();
            }
        },
        [closeDrawer]
    );
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', escCloseDrawer, false);
        } else {
            document.removeEventListener('keydown', escCloseDrawer, false);
        }
        return () => {
            document.removeEventListener('keydown', escCloseDrawer, false);
        };
    }, [isOpen, escCloseDrawer]);
    useEffect(() => {
        if (isOpen) {
            dispatch(setTriggerType({ triggerType: 'drawer' }));
            lockGlobalScroll();
        } else {
            dispatch(setTriggerType({ triggerType: 'dropdown' }));
            unlockGlobalScroll();
        }
    }, [isOpen, dispatch]);

    return (
        <React.Fragment>
            <div
                className={classNames(styles.overlay, {
                    [styles.showOverlay]: isOpen,
                    [styles.hideOverlay]: !isOpen,
                })}
                onClick={closeDrawer}
            />
            <div
                className={classNames(styles.drawer, {
                    [styles.entering]: isOpen,
                    [styles.exiting]: !isOpen,
                })}
            >
                <div className={styles.container}>
                    <div className={styles.scrollArea}>
                        <div className={styles.closeButtonContainer}>
                            <div className={styles.closeButton} onClick={closeDrawer}>
                                <Close />
                            </div>
                        </div>
                        <SbRespRefineMenuDrawerHeader itemSearch={itemSearch} />
                        <div className={styles.filters}>
                            <SbRespRefineMenuFilterSortFacet itemSearch={itemSearch} />
                            {/*
                            // @ts-ignore expects injected intl prop */}
                            <SbSharedRefineMenuFilters
                                itemSearch={itemSearch}
                                isClient={isClient}
                                user={user}
                                currency={currency}
                                viewer={viewer}
                                filterMap={sbRespRefineMenuFiltersMap}
                                filters={filters}
                                isTrade={isTrade}
                                initiallyExpanded={() => false}
                            />
                            {itemSearch?.pageType !== pageTypeConstants.DESIGN && (
                                <SbSharedRefineMenuDisplayPrefsSelect
                                    user={user}
                                    filters={filters}
                                    itemSearch={itemSearch}
                                    isClient={isClient}
                                />
                            )}
                        </div>
                    </div>
                    <Button onClick={closeDrawer} dataTn="refine-menu-drawer-view-results">
                        <FormattedMessage
                            id="SbRespRefineMenuDrawer.viewResults"
                            defaultMessage="VIEW {totalResults, plural, one {# RESULT} other {# RESULTS}}"
                            values={{ totalResults }}
                        />
                    </Button>
                </div>
            </div>
        </React.Fragment>
    );
};

export const SbRespRefineMenuDrawer = createFragmentContainer(SbRespRefineMenuDrawerComponent, {
    itemSearch: graphql`
        fragment SbRespRefineMenuDrawer_itemSearch on ItemSearchQueryConnection {
            ...SbSharedRefineMenuFilters_itemSearch
            ...SbRespSortItems_itemSearch
            ...SbSharedRefineMenuSearchWithin_itemSearch
            ...SbRespRefineMenuDrawerHeader_itemSearch
            pageType
            totalResults
            filters {
                ...SbSharedRefineMenuFilters_filters
                ...SbSharedRefineMenuDisplayPrefsSelect_filters
            }
            ...SbRespRefineMenuFilterSortFacet_itemSearch
            ...SbSharedRefineMenuDisplayPrefsSelect_itemSearch
        }
    `,
    user: graphql`
        fragment SbRespRefineMenuDrawer_user on User {
            ...SbSharedRefineMenuFilters_user
            ...SbSharedRefineMenuDisplayPrefsSelect_user
        }
    `,
    viewer: graphql`
        fragment SbRespRefineMenuDrawer_viewer on Viewer {
            ...SbSharedRefineMenuFilters_viewer
        }
    `,
});
